/*! JointJS+ v4.0.1 - HTML5 Diagramming Framework

Copyright (c) 2024 client IO

 2024-04-11 


This Source Code Form is subject to the terms of the JointJS+ License
, v. 2.0. If a copy of the JointJS+ License was not distributed with this
file, You can obtain one at https://www.jointjs.com/license
 or from the JointJS+ archive as was distributed by client IO. See the LICENSE file.*/


import { dia, format, shapes, ui } from '@joint/plus';

import { actionCreator } from '../redux/helpers/actionCreator';
import JointPlusService from '../services/joint-plus.service';
import { PADDING_L, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from '../theme';
import { stencilConfig } from './config/stencil.config';
import { SharedEvents } from './controller';
import { ShapeTypesEnum } from './shapes/app.shapes';
import { addCellTools } from './tools';

// Selection

export function setSelection(service: JointPlusService, selection: dia.Cell[]): void {
    const { paper, selection: previousSelection, dispatch } = service;
    paper.removeTools();
    previousSelection.forEach(cell => {
        const cellView = cell.findView(paper);
        if (cellView) {
            cellView.vel.removeClass('selected');
        }
    });
    service.selection = selection;
    selection.forEach(cell => {
        const cellView = cell.findView(paper);
        if (cellView) {
            cellView.vel.addClass('selected');
            addCellTools(cellView);
        }
    });
    dispatch(actionCreator(SharedEvents.SELECTION_CHANGED, selection));
}

export function removeSelection(service: JointPlusService) {
    const { selection, graph } = service;
    if (selection.length === 0) return;
    graph.removeCells(selection);
}

// Zooming

export function zoomToFit(service: JointPlusService) {
    const { scroller } = service;
    scroller.zoomToFit({
        minScale: ZOOM_MIN,
        maxScale: ZOOM_MAX,
        scaleGrid: ZOOM_STEP,
        useModelGeometry: true,
        padding: PADDING_L
    });
}

export function zoomIn(service: JointPlusService) {
    const { scroller } = service;
    scroller.zoom(ZOOM_STEP, {
        min: ZOOM_MIN,
        max: ZOOM_MAX,
    });
}

export function zoomOut(service: JointPlusService) {
    const { scroller } = service;
    scroller.zoom(-ZOOM_STEP, {
        min: ZOOM_MIN,
        max: ZOOM_MAX,
    });
}

// Import / Export

export function exportToPNG(service: JointPlusService): void {
    const { paper } = service;
    paper.hideTools();
    // Dump all views that are not in the viewport
    paper.dumpViews();
    format.toPNG(paper, (dataURL: string): void => {
        paper.showTools();
        openImageDownloadDialog(service, dataURL);
    }, {
        padding: 10,
        useComputedStyles: false
    });
}

export function openImageDownloadDialog(service: JointPlusService, dataURL: string, fileName: string = 'Joint'): void {
    const { keyboard, controllers } = service;
    const { keyboard: keyboardCtrl } = controllers;
    keyboardCtrl.stopListening();
    const lightbox = new ui.Lightbox({
        image: dataURL,
        downloadable: true,
        fileName
    });
    lightbox.on('action:close', () => {
        keyboardCtrl.startListening();
    });
    lightbox.listenTo(keyboard, 'escape', () => {
        keyboardCtrl.startListening();
        lightbox.close();
    });
    lightbox.open();
}

export function importGraphFromJSON(service: JointPlusService, json: any): void {
    setSelection(service, []);
    const { graph, history } = service;
    const shapeTypes = Object.values(ShapeTypesEnum);
    history.reset();
    try {
        if (json.cells.some((cell: any) => !shapeTypes.includes(cell.type))) {
            throw new Error('Invalid JSON: Unknown Cell Type');
        }
        graph.fromJSON(json);
    } catch (e) {
        // Invalid JSON format
    }
}

// Stencil

export function loadStencilShapes(service: JointPlusService): void {
    const { stencil } = service;
    const stencilShapes = stencilConfig.shapes.map(shape => new (shapes.stencil as any)[shape.name](shape));
    stencil.load(stencilShapes);

    // Fix stencil scroll (09/10/2026) — barra de rolagem aparece mas trunca em ~Tag;
    // caixas Note / BusinessHours / NativeAction / InteractiveList ficam invisíveis.
    // Causa: o `dia.Paper` interno de cada group do stencil nasce com altura FIXA
    // (default do JointJS Plus). Mesmo com o `.content` externo tendo overflow-y:auto,
    // o paper SVG não cresce sozinho pra caber todos os shapes carregados — então o
    // scroll chega ao fim do paper e não mostra o resto.
    //
    // Correção: logo após `stencil.load`, chama `fitToContent` em CADA paper do
    // stencil (há 1 por grupo; sem config de groups, é 1 só). O método é API pública
    // do dia.Paper e redimensiona o SVG pra encaixar bounding-box de todas as cells.
    // Zero mexer na config do plugin, zero refactor no CSS. 100% retrocompat.
    try {
        const papers = (stencil as any).papers ?? {};
        Object.keys(papers).forEach((groupName) => {
            const paper = papers[groupName];
            if (paper && typeof paper.fitToContent === 'function') {
                paper.fitToContent({ padding: 10, allowNewOrigin: 'any' });
            }
        });
    } catch (err) {
        // Silencioso — se a API mudar numa versão futura do JointJS Plus, cai no
        // comportamento antigo (truncado) mas não quebra o editor.
        // eslint-disable-next-line no-console
        console.warn('[stencil] fitToContent falhou:', err);
    }
}

// Paper

export function updateLinksRouting(service: JointPlusService): void {
    const { paper, graph } = service;
    graph.getLinks().forEach(link => {
        const linkView = link.findView(paper) as dia.LinkView;
        if (linkView) {
            linkView.requestConnectionUpdate();
        }
    });
}

// History

export function undoAction(service: JointPlusService) {
    const { history } = service;
    history.undo();
}

export function redoAction(service: JointPlusService) {
    const { history } = service;
    history.redo();
}
